/**
 * GuardrailPrompt — inline first-time permission prompt.
 * Shows when the agent encounters an action type for the first time:
 * "Always auto-run / Ask me each time / Never allow"
 */
import { CircleDot, Circle, CircleDashed } from "lucide-react";
import { cn } from "@/lib/utils";
import { AgentOrb } from "./AgentOrb";

export type GuardrailLevel = "auto" | "ask" | "never";

interface GuardrailPromptProps {
  actionLabel: string;
  onChoose: (level: GuardrailLevel) => void;
  className?: string;
}

const LEVEL_META: Record<
  GuardrailLevel,
  {
    label: string;
    icon: typeof CircleDot;
    sub: string;
    textColor: string;
    hoverBg: string;
    hoverBorder: string;
  }
> = {
  auto: {
    label: "Always run",
    icon: CircleDot,
    sub: "I'll just do it",
    textColor: "text-green2",
    hoverBg: "hover:bg-green4",
    hoverBorder: "hover:border-green2",
  },
  ask: {
    label: "Ask me first",
    icon: CircleDashed,
    sub: "I'll check with you",
    textColor: "text-blue2",
    hoverBg: "hover:bg-blue4",
    hoverBorder: "hover:border-blue2",
  },
  never: {
    label: "Never",
    icon: Circle,
    sub: "I can only suggest",
    textColor: "text-red2",
    hoverBg: "hover:bg-red4",
    hoverBorder: "hover:border-red2",
  },
};

export const GuardrailPrompt = ({
  actionLabel,
  onChoose,
  className,
}: GuardrailPromptProps) => {
  return (
    <div className={cn("mt-2 rounded-xl overflow-hidden border border-gray6 bg-white", className)}>
      <div className="px-3.5 py-3 flex items-center gap-2.5 bg-gradient-to-r from-orange4 to-white border-b border-orange3/40">
        <AgentOrb state="idle" size={20} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-bold text-black1">
            First time: {actionLabel}
          </div>
          <div className="text-[10.5px] text-orange1">
            How should I handle this going forward?
          </div>
        </div>
      </div>

      <div className="p-3 space-y-1.5">
        {(["auto", "ask", "never"] as GuardrailLevel[]).map((k) => {
          const m = LEVEL_META[k];
          const Icon = m.icon;
          return (
            <button
              key={k}
              onClick={() => onChoose(k)}
              className={cn(
                "w-full p-2.5 rounded-lg flex items-center gap-3 text-left",
                "cursor-pointer transition-all duration-150",
                "bg-white border-[1.5px] border-gray6",
                m.hoverBg,
                m.hoverBorder,
                "hover:shadow-sm"
              )}
            >
              <Icon className={cn("h-4 w-4 shrink-0", m.textColor)} />
              <div className="flex-1 min-w-0">
                <div className={cn("text-[12.5px] font-semibold", m.textColor)}>
                  {m.label}
                </div>
                <div className="text-[10.5px] text-gray3">{m.sub}</div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="px-3.5 py-2 bg-gray7 border-t border-gray6">
        <span className="text-[10px] text-gray3">
          You can change this anytime in Settings → Agent permissions
        </span>
      </div>
    </div>
  );
};
